import React, { useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Schedule from '@mui/icons-material/Schedule';
import RepeatIcon from '@mui/icons-material/Repeat';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PostPreview from './PostPreview';
import { parseApiUtcDate } from '../utils/datetime';
import {
  getPostPublishError,
  isPostPublishInFlight,
} from '../utils/postPublishErrors';

const locales = {
  'en-US': enUS,
};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const getStatusColor = (status) => {
  switch (status) {
    case 'draft':
      return 'default';
    case 'scheduled':
      return 'warning';
    case 'published':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

const getPublishStatusLabel = (post) => {
  const publishStatus = String(post?.publish_status || '').toLowerCase();
  if (post?.status === 'failed' || publishStatus === 'failed') return 'Failed';
  if (post?.status === 'published' || publishStatus === 'published') return 'Published';
  if (isPostPublishInFlight(post)) return publishStatus === 'queued' ? 'Queued' : 'Publishing';
  if (post?.status === 'scheduled') return 'Scheduled';
  return publishStatus || post?.status || 'Unknown';
};

const getPublishStatusColor = (post) => {
  const publishStatus = String(post?.publish_status || '').toLowerCase();
  if (post?.status === 'failed' || publishStatus === 'failed') return 'error';
  if (post?.status === 'published' || publishStatus === 'published') return 'success';
  if (isPostPublishInFlight(post)) return 'info';
  if (post?.status === 'scheduled') return 'warning';
  return 'default';
};

const isRecurringPost = (post) =>
  post.recurring_schedule_id || post.is_recurring;

/**
 * Calendar day for a post — never default to "now" (that stacks every unscheduled
 * post onto today's cell). Prefer schedule, then publish time for published posts.
 */
function getCalendarAnchorDate(post) {
  if (post.scheduled_time) {
    return parseApiUtcDate(post.scheduled_time);
  }
  const st = (post.status || '').toLowerCase();
  if (st === 'published' && post.published_time) {
    return parseApiUtcDate(post.published_time);
  }
  return null;
}

function eventStyleGetter(event) {
  let backgroundColor = '#58a6ff';
  if (event.status === 'failed') backgroundColor = '#f85149';
  else if (event.status === 'published') backgroundColor = '#238636';
  else if (event.status === 'scheduled') backgroundColor = '#f78166';
  else if (event.status === 'draft') backgroundColor = '#7d8590';
  return {
    style: {
      backgroundColor,
      borderRadius: '6px',
      color: '#ffffff',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      fontWeight: 600,
      fontSize: '0.9em',
      padding: '4px 8px',
      minHeight: 28,
      overflow: 'hidden',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    },
  };
}

const PostCalendar = ({
  posts,
  onEdit,
  onDelete,
  onView,
  onPreview,
  onReschedule,
}) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    post: null,
  });
  const [view, setView] = useState(Views.MONTH);

  // Map posts to calendar events (only posts with a real anchor date)
  const events = useMemo(() => {
    const out = [];
    for (const post of posts) {
      const start = getCalendarAnchorDate(post);
      if (!start) continue;
      const end = new Date(start.getTime() + 30 * 60 * 1000);
      out.push({
        ...post,
        title: post.title || 'Untitled',
        start,
        end,
        allDay: false,
      });
    }
    return out;
  }, [posts]);

  // Custom event rendering
  function Event({ event }) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          overflow: 'hidden',
        }}
      >
        <Tooltip title={event.platform}>
          <span>
            {isRecurringPost(event) && (
              <RepeatIcon fontSize="small" sx={{ color: '#58a6ff' }} />
            )}
            {event.status === 'failed' && (
              <ErrorOutlineIcon fontSize="small" sx={{ color: '#f85149' }} />
            )}
          </span>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{
            color: '#ffffff',
            fontWeight: 600,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            maxWidth: 120,
          }}
        >
          {event.title}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { xs: '60vh', md: '80vh' },
        minHeight: { xs: 360, md: 600 },
        maxWidth: '100%',
        overflowX: 'auto',
        background: '#161b22',
        borderRadius: 2,
        p: { xs: 1, sm: 2 },
        border: '1px solid #30363d',
        '& .rbc-calendar': {
          color: '#f0f6fc',
          fontFamily: 'inherit',
        },
        '& .rbc-toolbar': {
          marginBottom: '16px',
          padding: '8px 0',
          borderBottom: '1px solid #30363d',
          flexWrap: 'wrap',
          gap: 1,
        },
        '& .rbc-toolbar-label': {
          fontSize: { xs: '1.1rem', sm: '1.5rem' },
          fontWeight: 600,
          color: '#f0f6fc',
        },
        '& .rbc-btn-group': {
          backgroundColor: '#21262d',
          border: '1px solid #30363d',
          borderRadius: '6px',
          overflow: 'hidden',
        },
        '& .rbc-btn-group button': {
          backgroundColor: 'transparent',
          border: 'none',
          color: '#7d8590',
          padding: '6px 10px',
          fontSize: '0.875rem',
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: '#30363d',
            color: '#f0f6fc',
          },
          '&:active': {
            backgroundColor: '#58a6ff',
            color: '#ffffff',
          },
          '&.rbc-active': {
            backgroundColor: '#58a6ff',
            color: '#ffffff',
            boxShadow: 'none',
          },
        },
        '& .rbc-btn-group button:not(:last-child)': {
          borderRight: '1px solid #30363d',
        },
        '& .rbc-header': {
          backgroundColor: '#21262d',
          color: '#f0f6fc',
          fontWeight: 600,
          padding: '12px 8px',
          borderBottom: '1px solid #30363d',
          fontSize: '0.875rem',
        },
        '& .rbc-month-view': {
          border: '1px solid #30363d',
          borderRadius: '6px',
          overflow: 'hidden',
        },
        '& .rbc-month-row': {
          borderBottom: '1px solid #30363d',
        },
        '& .rbc-month-row:last-child': {
          borderBottom: 'none',
        },
        '& .rbc-date-cell': {
          padding: '8px',
          color: '#f0f6fc',
          fontSize: '0.875rem',
          fontWeight: 500,
        },
        '& .rbc-off-range-bg': {
          backgroundColor: '#0d1117',
        },
        '& .rbc-off-range': {
          color: '#484f58',
        },
        '& .rbc-today': {
          backgroundColor: 'rgba(88, 166, 255, 0.1)',
        },
        '& .rbc-day-bg': {
          backgroundColor: '#161b22',
          borderRight: '1px solid #30363d',
          '&:hover': {
            backgroundColor: '#21262d',
          },
        },
        '& .rbc-day-bg:last-child': {
          borderRight: 'none',
        },
        '& .rbc-event': {
          backgroundColor: '#58a6ff',
          border: 'none',
          borderRadius: '4px',
          padding: '2px 6px',
          margin: '1px 2px',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
          },
        },
        '& .rbc-event-content': {
          color: '#ffffff',
          fontWeight: 600,
          fontSize: '0.8rem',
        },
        '& .rbc-show-more': {
          backgroundColor: 'transparent',
          color: '#58a6ff',
          border: 'none',
          padding: '2px 6px',
          fontSize: '0.75rem',
          fontWeight: 500,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(88, 166, 255, 0.1)',
          },
        },
        '& .rbc-week-view': {
          border: '1px solid #30363d',
          borderRadius: '6px',
          overflow: 'hidden',
        },
        '& .rbc-time-view': {
          border: '1px solid #30363d',
          borderRadius: '6px',
          overflow: 'hidden',
        },
        '& .rbc-time-header': {
          backgroundColor: '#21262d',
          borderBottom: '1px solid #30363d',
        },
        '& .rbc-time-content': {
          backgroundColor: '#161b22',
        },
        '& .rbc-timeslot-group': {
          borderBottom: '1px solid #30363d',
        },
        '& .rbc-time-slot': {
          color: '#7d8590',
          fontSize: '0.75rem',
        },
        '& .rbc-current-time-indicator': {
          backgroundColor: '#f85149',
        },
      }}
    >
      {events.length === 0 && posts.length > 0 && (
        <Typography
          variant="body2"
          sx={{ color: '#7d8590', mb: 1.5, px: 0.5 }}
        >
          Showing posts with a scheduled time, or published posts by their publish
          date. Drafts and other posts without a date appear in the Table tab.
        </Typography>
      )}
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '72vh', minHeight: 500 }}
        views={['month', 'week', 'day']}
        view={view}
        onView={setView}
        defaultView={Views.MONTH}
        popup
        eventPropGetter={eventStyleGetter}
        components={{ event: Event }}
        onSelectEvent={(event) => setSelectedEvent(event)}
        toolbar={true}
        selectable={false}
        longPressThreshold={10}
      />
      {/* Event Dialog */}
      <Dialog
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#161b22',
            border: '1px solid #30363d',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{ color: '#f0f6fc', borderBottom: '1px solid #30363d' }}
        >
          {selectedEvent?.title || 'Untitled Post'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedEvent && (
            <Box>
              {getPostPublishError(selectedEvent) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    mb: 2,
                    borderRadius: 1,
                    bgcolor: 'rgba(248, 81, 73, 0.12)',
                    border: '1px solid rgba(248, 81, 73, 0.35)',
                    color: '#f85149',
                  }}
                >
                  <ErrorOutlineIcon fontSize="small" />
                  <Typography variant="body2">
                    {getPostPublishError(selectedEvent)}
                  </Typography>
                </Box>
              )}
              <Typography variant="body1" sx={{ color: '#f0f6fc', mb: 2 }}>
                {selectedEvent.content}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={selectedEvent.platform}
                  size="small"
                  sx={{
                    bgcolor: '#21262d',
                    color: '#f0f6fc',
                    border: '1px solid #30363d',
                  }}
                />
                <Chip
                  label={selectedEvent.status}
                  color={getStatusColor(selectedEvent.status)}
                  size="small"
                  sx={{
                    bgcolor:
                      selectedEvent.status === 'published'
                        ? '#238636'
                        : selectedEvent.status === 'scheduled'
                        ? '#f78166'
                        : selectedEvent.status === 'failed'
                        ? '#f85149'
                        : '#7d8590',
                    color: '#ffffff',
                    fontWeight: 500,
                  }}
                />
                <Chip
                  label={`Publish: ${getPublishStatusLabel(selectedEvent)}`}
                  color={getPublishStatusColor(selectedEvent)}
                  size="small"
                  variant="outlined"
                  sx={{ color: '#ffffff', fontWeight: 500 }}
                />
                {isRecurringPost(selectedEvent) && (
                  <Chip
                    icon={<RepeatIcon />}
                    label="Recurring"
                    size="small"
                    sx={{
                      bgcolor: '#58a6ff',
                      color: '#ffffff',
                      '& .MuiChip-icon': { color: '#ffffff' },
                    }}
                  />
                )}
                {selectedEvent.status === 'failed' && (
                  <Chip
                    icon={<ErrorOutlineIcon />}
                    label="Failed"
                    size="small"
                    sx={{
                      bgcolor: '#f85149',
                      color: '#ffffff',
                      '& .MuiChip-icon': { color: '#ffffff' },
                    }}
                  />
                )}
              </Box>
              <PostPreview
                platform={selectedEvent.platform?.toLowerCase() || 'facebook'}
                content={selectedEvent.content}
                title={selectedEvent.title}
                media={selectedEvent.media_urls?.[0]}
                hashtags={selectedEvent.hashtags || []}
                user={{
                  name: selectedEvent.client?.name || '',
                  handle: '',
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #30363d' }}>
          <Button
            onClick={() => onEdit(selectedEvent)}
            startIcon={<EditIcon />}
            sx={{
              color: '#f78166',
              '&:hover': { bgcolor: 'rgba(247, 129, 102, 0.1)' },
            }}
          >
            Edit
          </Button>
          <Button
            onClick={() => {
              onReschedule(selectedEvent);
              setSelectedEvent(null);
            }}
            startIcon={<Schedule />}
            sx={{
              color: '#58a6ff',
              '&:hover': { bgcolor: 'rgba(88, 166, 255, 0.1)' },
            }}
          >
            Reschedule
          </Button>
          <Button
            onClick={() =>
              setPreviewDialog({ open: true, post: selectedEvent })
            }
            startIcon={<PreviewIcon />}
            sx={{
              color: '#58a6ff',
              '&:hover': { bgcolor: 'rgba(88, 166, 255, 0.1)' },
            }}
          >
            Preview
          </Button>
          <Button
            onClick={() => onDelete(selectedEvent.id)}
            startIcon={<DeleteIcon />}
            sx={{
              color: '#f85149',
              '&:hover': { bgcolor: 'rgba(248, 81, 73, 0.1)' },
            }}
          >
            Delete
          </Button>
          <Button
            onClick={() => setSelectedEvent(null)}
            sx={{
              color: '#7d8590',
              '&:hover': { bgcolor: '#21262d' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, post: null })}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#161b22',
            border: '1px solid #30363d',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{ color: '#f0f6fc', borderBottom: '1px solid #30363d' }}
        >
          Post Preview
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {previewDialog.post && (
            <PostPreview
              platform={
                previewDialog.post.platform?.toLowerCase() || 'facebook'
              }
              content={previewDialog.post.content}
              title={previewDialog.post.title}
              media={previewDialog.post.media_urls?.[0]}
              hashtags={previewDialog.post.hashtags || []}
              user={{
                name: previewDialog.post.client?.name || '',
                handle: '',
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #30363d' }}>
          <Button
            onClick={() => setPreviewDialog({ open: false, post: null })}
            sx={{
              color: '#7d8590',
              '&:hover': { bgcolor: '#21262d' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PostCalendar;
